<template>
    <div
        class="box"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
        :class="dragOver ? 'has-background-link-light' : 'has-background-fallback'"
        style="border: 2px dashed #888; transition: background-color 0.3s, border-color 0.3s;"
        :style="dragOver ? 'border-color: #3273dc; border-style: solid;' : 'border-color: rgba(128, 128, 128, 0.5);'"
    >
        <div v-if="processing" class="has-text-centered py-5">
            <div class="mb-4">
                <span class="icon is-large has-text-info">
                    <i class="mdi mdi-loading mdi-spin mdi-48px"></i>
                </span>
            </div>
            <p class="is-size-5 has-text-weight-medium mb-2 has-text-info">
                {{ $t('file_upload.pdf_file_processing') }}...
            </p>
            <p class="is-size-7 has-text-grey">
                {{ $t('file_upload.pdf_to_image_converting') }}
            </p>
        </div>

        <div v-else class="has-text-centered py-5">
            <div class="mb-3">
                <b-icon
                    icon="file-pdf-box"
                    size="is-large"
                    :type="dragOver ? 'is-link' : 'is-grey-light'">
                </b-icon>
            </div>

            <p class="is-size-5 has-text-weight-medium mb-2">
                {{ dragOver ? $t('file_upload.drop_now') : $t('file_upload.drop_or_click') }}
            </p>

            <div class="file is-centered">
                <label class="file-label">
                    <input
                        ref="pdfInput"
                        class="file-input"
                        type="file"
                        accept="application/pdf"
                        @change="onFileChange"
                        :disabled="processing"
                    >
                    <span class="file-cta">
                        <span class="file-icon"><i class="mdi mdi-upload"></i></span>
                        <span class="file-label">{{ $t('file_upload.select_file') }}</span>
                    </span>
                </label>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PdfUploader',
    data() {
        return {
            processing: false,
            dragOver: false,
        }
    },
    methods: {
        async onFileChange(event) {
            const file = event.target.files[0];

            if (!file) {
                return;
            }

            if (file.type !== 'application/pdf') {
                this.$toast.error(this.$t('file_upload.select_pdf_file'));
                return;
            }

            await this.processPdfFile(file);
        },

        async processPdfFile(file) {
            this.processing = true;

            const formData = new FormData();
            formData.append('pdf', file);

            try {
                const response = await axios.post('/pdf-to-images', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });

                const images = response.data.images;

                if (images && images.length > 0) {
                    const processedImages = images.map((base64Image, index) => {
                        const byteString = atob(base64Image.split(',')[1]);
                        const mimeString = base64Image.split(',')[0].split(':')[1].split(';')[0];
                        const ab = new ArrayBuffer(byteString.length);
                        const ia = new Uint8Array(ab);

                        for (let i = 0; i < byteString.length; i++) {
                            ia[i] = byteString.charCodeAt(i);
                        }

                        const blob = new Blob([ab], { type: mimeString });
                        const fileName = `${file.name.replace('.pdf', '')}_page_${index + 1}.png`;
                        const imageFile = new File([blob], fileName, { type: mimeString });

                        return {
                            file: imageFile,
                            preview: base64Image,
                            timestamp: '',
                            id: null
                        };
                    });

                    this.$emit('images-processed', processedImages);
                    this.$toast.success(this.$t('training.messages.new_pdf_slides') + ': ' + images.length);
                } else {
                    this.$toast.error(this.$t('file_upload.pdf_processing_error'));
                }
            } catch (error) {
                this.$toast.error(error.response?.data?.message || this.$t('file_upload.pdf_processing_error'));
            } finally {
                this.processing = false;

                if (this.$refs.pdfInput) {
                    this.$refs.pdfInput.value = '';
                }
            }
        },

        onDragOver(event) {
            event.preventDefault();
            this.dragOver = true;
        },

        onDragLeave(event) {
            event.preventDefault();
            this.dragOver = false;
        },

        async onDrop(event) {
            event.preventDefault();
            this.dragOver = false;

            if (this.processing) {
                return;
            }

            const files = event.dataTransfer.files;
            if (files.length === 0) {
                return;
            }

            const file = files[0];

            if (file.type !== 'application/pdf') {
                this.$toast.error(this.$t('file_upload.select_pdf_file'));
                return;
            }

            await this.processPdfFile(file);
        },
    }
}
</script>
